import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useHistory } from 'react-router-dom';
import { FaCheck, FaTimes, FaEye, FaUserPlus } from 'react-icons/fa';
import debounce from 'lodash.debounce';
import Table from '../../components/Table';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Select from '../../components/Select';
import InputSeason from '../../components/InputSeason/InputSeason';
import Paths from '../../constants/paths';
import parkingAPI from '../../api/parking';
import formatPin from '../../helpers/formatPin';
import translations from '../../translations/de';
import AssignOwnerModal from '../ParkingFields/AssignOwnerModal';

const TYPE_OPTIONS = [
    { label: translations.all, value: null },
    { label: translations.full_season, value: 'full_season' },
    { label: 'Einzelspiel', value: 'single_game' },
];

// Display name of the app user a pass is assigned to (owner), if any.
const ownerName = (p) =>
    p.user ? [p.user.first_name, p.user.last_name].filter(Boolean).join(' ').trim() || p.user.email || `#${p.user.id}` : '';

// Global overview + search of ALL parking cards (mirrors the Abos list): filter
// by season / field / type, search by PIN, by the code printed under the QR on
// a handed-out card, or by contact / owner.
const ParkingPassesOverview = ({ store }) => {
    const history = useHistory();
    const [passes, setPasses] = useState([]);
    const [fields, setFields] = useState([]);
    const [seasonId, setSeasonId] = useState(null);
    const [fieldId, setFieldId] = useState(null);
    const [type, setType] = useState(null);
    const [search, setSearch] = useState('');
    const [reload, setReload] = useState(0);
    const [ownerModalPass, setOwnerModalPass] = useState(null);

    useEffect(() => {
        // ParkingPassesTab wraps children in <Page loading={!store.dataLoaded}>.
        store && !store.dataLoaded && store.setDataLoaded();
        parkingAPI
            .getFields()
            .then((res) => setFields(res.data || []))
            .catch(() => setFields([]));
    }, [store]);

    useEffect(() => {
        const load = async () => {
            try {
                const params = {};
                if (seasonId) params.season_id = seasonId;
                if (fieldId) params.parking_field_id = fieldId;
                if (type) params.type = type;
                if (search) params.search = search;
                const res = await parkingAPI.getPasses({ params });
                setPasses(res.data || []);
            } catch (e) {
                setPasses([]);
            }
        };
        load();
    }, [seasonId, fieldId, type, search, reload]);

    const onSearchChange = debounce((e) => setSearch(e.target.value), 500);

    const columns = [
        { Header: 'ID', accessor: 'id', Cell: ({ value }) => <div>{value}</div> },
        {
            Header: translations.parking_field,
            accessor: 'field',
            Cell: ({ value }) => <div>{value}</div>,
        },
        { Header: translations.parking_code, accessor: 'code', Cell: ({ value }) => <div>{value}</div> },
        {
            Header: translations.type,
            accessor: 'type',
            Cell: ({ value }) => <div>{value}</div>,
        },
        {
            Header: translations.assigned,
            accessor: 'assigned',
            Cell: ({ value }) => (
                <div>{value ? <FaCheck className="confirm" /> : <FaTimes className="error" />}</div>
            ),
        },
        { Header: 'Kontakt', accessor: 'contact', Cell: ({ value }) => <div>{value}</div> },
        { Header: 'Besitzer', accessor: 'owner', Cell: ({ value }) => <div>{value || '-'}</div> },
        {
            Header: '',
            accessor: 'action',
            Cell: ({ row }) => (
                <div className="button-cell">
                    <Button
                        title="Zuteilen"
                        onClick={() => setOwnerModalPass(passes.find((p) => p.id === row.original.id))}
                    >
                        <FaUserPlus />
                    </Button>
                    <Button
                        title={translations.show}
                        onClick={() => history.push(Paths.PARKING_PASS.replace(':pass_id', row.original.id))}
                    >
                        <FaEye />
                    </Button>
                </div>
            ),
        },
    ];

    return (
        <div style={{ width: '100%' }}>
            <div style={{ margin: '0 2.5% 10px' }}>
                <h2 style={{ margin: 0 }}>{translations.parking_passes}</h2>
            </div>
            <div className="tab-controls">
                <Input
                    label={translations.search}
                    placeholder="PIN, QR-Code, Kontakt, Besitzer"
                    onChange={onSearchChange}
                />
                <InputSeason selectedSeason={seasonId} setSelectedSeason={setSeasonId} />
                <Select
                    label={translations.parking_field}
                    defaultValue={{ label: translations.all, value: null }}
                    options={[
                        { label: translations.all, value: null },
                        ...fields.map((f) => ({ label: f.name, value: f.id })),
                    ]}
                    isSearchable={false}
                    onChange={(e) => setFieldId(e.value)}
                />
                <Select
                    label={translations.type}
                    defaultValue={TYPE_OPTIONS[0]}
                    options={TYPE_OPTIONS}
                    isSearchable={false}
                    onChange={(e) => setType(e.value)}
                />
            </div>

            <Table
                tableName={translations.parking_passes}
                isRowClickable={false}
                columns={columns}
                data={passes.map((p) => ({
                    id: p.id,
                    field: p.parking_field?.name ?? '-',
                    code: formatPin(p.assign_token),
                    type: p.type === 'full_season' ? translations.full_season : 'Einzelspiel',
                    assigned: !!p.user_id,
                    contact: [p.first_name, p.last_name].filter(Boolean).join(' ') || '-',
                    owner: ownerName(p),
                }))}
            />

            {ownerModalPass && (
                <AssignOwnerModal
                    passId={ownerModalPass.id}
                    currentOwner={ownerName(ownerModalPass)}
                    onClose={() => setOwnerModalPass(null)}
                    onAssigned={() => {
                        setOwnerModalPass(null);
                        setReload((r) => r + 1);
                    }}
                />
            )}
        </div>
    );
};

export default observer(ParkingPassesOverview);
